<template>
  <div class="fts-automation-designer">
    <div class="automation-playground">
      <div class="automation-toolbar">
        <div class="automation-toolbar__control">
          <div
            class="automation-toolbar-menu"
            :class="{ collapsed: controlCollapse }"
            @click="controlCollapse = !controlCollapse"
          >
            <div class="automation-toolbar-menu__label">Try It</div>
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="automation-toolbar-menu__toggle"
            />
          </div>

          <fts-transition-expand>
            <div v-if="!controlCollapse">
              <div class="automation-debug-selection">
                <div class="automation-debug-select automation-debug--entry">
                  <div class="fts-form-label">Entry</div>
                  <fts-dropdown
                    :form-data="data"
                    :item="{
                      displayName: 'Test Locus',
                      fieldName: 'testLocus',
                      type: 7,
                      key: 'testLocusId',
                      connectionId: moduleId,
                    }"
                    :module-design="moduleId"
                  />
                </div>

                <div class="automation-debug-select automation-debug--activity"
                 v-if="data.testLocusId">
                  <div class="fts-form-label">Activity</div>
                  <fts-dropdown
                    :form-data="data"
                    :item="{
                      displayName: 'Test Activity',
                      fieldName: 'testLocusDetail',
                      type: 7,
                      key: 'testLocusDetailId',
                      connectionId: moduleId,
                    }"
                    :module-design="moduleId"
                    locus-detail
                    :locus-id="data.testLocusId"
                  />
                </div>
              </div>

              <div class="automation-debug-panel">
                <fts-btn
                  icon
                  text
                  @click="debugAutomation(0)"
                >
                  <font-awesome-icon :icon="['fas', 'play']" />
                </fts-btn>
                <fts-btn
                  icon
                  text
                  @click="debugAutomation(1)"
                >
                  <font-awesome-icon :icon="['fas', 'step-forward']" />
                </fts-btn>
                <fts-btn
                  icon
                  text
                  @click="debugAutomation(2)"
                >
                  <font-awesome-icon :icon="['fas', 'stop']" />
                </fts-btn>
              </div>
            </div>
          </fts-transition-expand>
        </div>

        <div class="automation-toolbar__node">
          <div
            class="automation-toolbar-menu"
            :class="{ collapsed: nodeCollapse }"
            @click="nodeCollapse = !nodeCollapse"
          >
            <div class="automation-toolbar-menu__label">Tools</div>
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="automation-toolbar-menu__toggle"
            />
          </div>

          <fts-transition-expand>
            <div v-if="!nodeCollapse">
              <div
                v-for="node in nodes"
                :key="node.id"
                class="automation-toolbar-node"
                :class="`automation-toolbar-node-${node.theme}`"
                draggable="true"
                :data-node="node.id"
                @dragstart="drag"
              >
                <i class="automation-toolbar-node__icon" :class="node.icon" v-if="node.icon" />
                <div class="automation-toolbar-node__label">{{ node.name }}</div>
              </div>
            </div>
          </fts-transition-expand>
        </div>

        <div class="automation-toolbar__console">
          <div
            class="automation-toolbar-menu"
            :class="{ collapsed: consoleCollapse }"
            @click="consoleCollapse = !consoleCollapse"
          >
            <div class="automation-toolbar-menu__label">
              Variables
              <span v-if="debugPayload && debugPayload.debugPayload"
                >({{ Object.keys(debugPayload.debugPayload).length }})</span
              >
            </div>
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="automation-toolbar-menu__toggle"
            />
          </div>

          <fts-transition-expand>
            <div class="fts-scrollbar" v-if="!consoleCollapse">
              <fts-automation-variables
                v-if="debugPayload && debugPayload.debugPayload"
                :variable="debugPayload.debugPayload"
                style="margin: 0 1rem 1rem"
              />
            </div>
          </fts-transition-expand>
        </div>
      </div>
      <div id="drawflow" style="perspective: 0; width: 100%; height: 100%; background: #fafafa;" @drop="drop" @dragover="allowDrop"></div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

import commons from "../../commons/commons";
import VueJsonPretty from "vue-json-pretty";
import { automationDesignStore } from "../../store/automationDesignStore";

import automationBlocks from "../../commons/automationBlocks";
import Drawflow from "../../../external/drawflow";
import styleDrawflow from "../../../external/scss/drawflow.scss";
import FTSTransitionExpand from "../../elements/FTSTransitionExpand";
import FTSDropdown from "../../elements/FTSDropdown";
import FTSBtn from "../../elements/FTSBtn";
import FTSAutomationVariables from "./FTSAutomationVariables";

export default {
  props: {
    data: Object,
    moduleId: String,
    isForm: Boolean
  },
  data: () => ({
    exportDataModel: {},
    editor: null,
    /* toolbar */
    /* nodes */
    nodes: [
      { id: "sequence", name: "Actions", theme: 'action', icon: "fts-icon-automation-action" },
      { id: "ifElse", name: "Condition", theme: 'condition', icon: "fts-icon-automation-condition" },
      { id: "forEach", name: "For", theme: 'for', icon: "fts-icon-automation-for" },
    ],
    /* collapse */
    nodeCollapse: false,
    controlCollapse: false,
    consoleCollapse: false,
    /* formData */
    formData: {},
    debugPayload: null,
  }),
  components: {
    "fts-transition-expand": FTSTransitionExpand,
    "fts-btn": FTSBtn,
    "fts-dropdown": FTSDropdown,
    "fts-automation-variables": FTSAutomationVariables,
    VueJsonPretty,
  },
  methods: {
    async debugAutomation(debugType) {
      if (debugType == 2) {
        if (this.debugPayload) {
          this.debugPayload.nodeId = null;
          this.debugPayload.actionId = null;
          this.debugPayload.debugPayload = null;
          this.debugPayload.forEach = null;
        }

        window.eventBus.$emit("refreshAutomationBlocks");
        return;
      }

      let debugPayloadClone = _.cloneDeep(this.debugPayload);
      let result = await this.$store.dispatch("testAutomationBlock", {
        vectorId: this.moduleId,
        automationBlocks: this.data,
        debugPayload: debugPayloadClone,
        debug: debugType,
        isForm: this.isForm,
      });
      this.debugPayload = result.data;
    },
    drag(ev) {
      if (ev.type === "touchstart") {
        mobile_item_selec = ev.target
          .closest(".drag-drawflow")
          .getAttribute("data-node");
      } else {
        ev.dataTransfer.setData("node", ev.target.getAttribute("data-node"));
      }
    },
    drop(ev) {
      let node = ev.dataTransfer.getData("node");
      let editor = this.editor;

      let pos_x =
        ev.clientX *
          (editor.precanvas.clientWidth /
            (editor.precanvas.clientWidth * editor.zoom)) -
        editor.precanvas.getBoundingClientRect().x *
          (editor.precanvas.clientWidth /
            (editor.precanvas.clientWidth * editor.zoom));
      let pos_y =
        ev.clientY *
          (editor.precanvas.clientHeight /
            (editor.precanvas.clientHeight * editor.zoom)) -
        editor.precanvas.getBoundingClientRect().y *
          (editor.precanvas.clientHeight /
            (editor.precanvas.clientHeight * editor.zoom));

      automationBlocks.addNode(
        this.editor,
        node,
        { id: commons.nanoID() },
        pos_x,
        pos_y
      );
    },
    allowDrop(ev) {
      ev.preventDefault();
    },
    exportModel() {
      var exportdata = this.editor.export();
      this.data.blocks = exportdata.drawflow.Home.data;
      this.exportDataModel = _.cloneDeep(this.data);
      Object.values(this.exportDataModel).forEach((node) => {
        if (node.name != "start") {
          delete node.data.variables;
          delete node.data.priorVariables;

          if (node.data.actions) {
            node.data.actions.forEach((action) => {
              delete action.variables;
              delete action.priorVariables;
            });
          }
        }
      });

      return this.exportDataModel;
    },
    loadDiagram() {
      let vm = this;
      automationDesignStore.dispatch("loadDiagram", vm.data.blocks);

      var id = document.getElementById("drawflow");
      try {
        this.editor = new Drawflow(id, Vue, vm);
        this.editor.drawflow = {
          drawflow: {
            Home: {
              data: vm.data.blocks,
            },
          },
        };
        automationBlocks.registerNodes(this.editor);
        this.editor.start();

        this.editor.on("nodeCreated", function (id) {
          console.log("Node created " + id);
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        this.editor.on("nodeRemoved", function (id) {
          console.log("Node created " + id);
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        this.editor.on("connectionCreated", function (id) {
          console.log("Connection created " + id);
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        this.editor.on("connectionRemoved", function (id) {
          console.log("Connection removed " + id);
          // vm.$forceUpdate();
          automationDesignStore.dispatch("updateVariables");
          window.eventBus.$emit("refreshAutomationBlocks");
        });

        window.eventBus.$emit("refreshAutomationBlocks");
      } catch (err) {
        console.log(err);
      }
    },
  },
  computed: {
    vectorId() {
      return this.$store.state.vectorId;
    },
  },
  watch: {
    debugPayload: function (newValue, oldValue) {
      automationDesignStore.dispatch("debugPayload", newValue);
      window.eventBus.$emit("refreshAutomationBlocks");
    },
  },
  async mounted() {
    let vm = this;
    vm.loadDiagram();
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-automation-designer {
  .automation-toolbar {
    width: 300px;
    min-width: 300px;
    max-width: 300px;
    display: flex;
    flex-direction: column;
    border: 1px solid $borderColor;
    .automation-toolbar__control {
      border-bottom: 1px solid $borderColor;
      .automation-debug-selection {
        margin-left: 1rem;
        margin-right: 1rem;
        .automation-debug-select {
          margin-bottom: 1.25rem;
          &:not(:first-child) {
            margin-top: 1.25rem;
          }
        }
      }
      .automation-debug-panel {
        display: flex;
        align-items: center;
        margin: 1rem;
      }
    }
    .automation-toolbar__node {
      border-bottom: 1px solid $borderColor;
      .automation-toolbar-node {
        display: flex;
        align-items: center;
        margin: 0 0.75rem 0.575rem;
        padding: 0.575rem 0.75rem;
        border-radius: 0.5rem;
        font-size: .875rem;
        font-weight: 500;
        &:hover {
          cursor: grab;
        }
        &.automation-toolbar-node-action {
          border: 1px solid var(--fts-gray-3);
          background: var(--fts-block-action-background);
        }
        &.automation-toolbar-node-condition {
          border: 1px solid var(--fts-block-condition-background);
          background: var(--fts-block-condition-background);
          color: #fff;
        }
        &.automation-toolbar-node-for {
          border: 1px solid var(--fts-block-for-background);
          background: var(--fts-block-for-background);
          color: #fff;
        }
        .automation-toolbar-node__icon {
          margin-right: .375rem;
        }
        .automation-toolbar-node__label {
          //
        }
      }
    }
    .automation-toolbar__console {
      flex: 1;
      display: flex;
      flex-direction: column;
      overflow: hidden;
    }
    .automation-toolbar-menu {
      display: flex;
      justify-content: space-between;
      padding: 0.75rem 1rem;
      cursor: pointer;
      &:not(.collapsed) {
        .automation-toolbar-menu__toggle {
          transform: rotate(90deg);
        }
      }
      .automation-toolbar-menu__label {
        margin-right: 0.75rem;
        font-size: .875rem;
        font-weight: 600;
      }
      .automation-toolbar-menu__toggle {
        flex-shrink: 0;
        transition: all 0.3s;
      }
    }
  }
  .automation-playground {
    display: flex;
    height: 800px;
    max-height: 800px;
  }
  .drawflow {
    .drawflow-node {
      &.start {
        .automation-node {
          background: var(--fts-block-start-background);
          color: #fff;
        }
        .output_1 {
          &::before {
            content: "start";
            color: var(--fts-block-gray-5);
          }
        }
      }
      &.sequence {
        .automation-node {
          background: var(--fts-block-action-background);
        }
        .output_1 {
          &::before {
            content: "next";
          }
        }
      }
      &.if-else {
        .automation-node {
          background: var(--fts-block-condition-background);
          color: #fff;
        }
        .output_1 {
          &::before {
            content: "true";
            color: var(--fts-block-gray-5);
          }
        }
        .output_2 {
          &::before {
            content: "false";
            color: var(--fts-block-gray-5);
          }
        }
      }
      &.for-each {
        .automation-node {
          background: var(--fts-block-for-background);
          color: #fff;
        }
        .output_1 {
          &::before {
            content: "each";
            color: var(--fts-block-gray-5);
          }
        }
        .output_2 {
          &::before {
            content: "next";
            color: var(--fts-block-gray-5);
          }
        }
      }
      .drawflow_content_node {
        border-radius: inherit;
        > * {
          border-top-left-radius: inherit;
          border-top-right-radius: inherit;
        }
        .node-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: .575rem 1rem;
          &.node-header--center {
            justify-content: center;
          }
          .node-header__label {
            display: flex;
            align-items: center;
            .node-header-icon {
              margin-right: .375rem;
            }
            .node-header-title {
              font-size: 1rem;
              font-weight: 500;
            }
          }
          .node-header__action {
            display: flex;
            margin-left: 0.75rem;
            > *:not(:last-child) {
              margin-right: 0.575rem;
            }
          }
        }
        .node-body {
          padding: 0 1rem 1.5rem;
        }
      }
      .output {
        &::before {
          @include absolute-center-x;
          top: -1rem;
          font-size: 0.75rem;
          color: $grayLightColor;
        }
      }
    }
  }
  .automation-node {
    width: 400px;
    padding-bottom: 1.5rem;
    border: 2px solid transparent;
    border-radius: inherit;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
    &:hover,
    &.automation-node--debug {
      .node-header__debugpoint {
        display: block;
      }
    }
    &.automation-node--debugging {
      border-color: var(--fts-block-danger);
    }
    .node-header__debugpoint {
      display: none;
    }
  }
  .automation-formula-form-control {
    padding: .375rem;
    border: 1px solid var(--fts-form-control-border);
    border-radius: .25rem;
    background: var(--fts-form-control-background);
  }
}

:root {
  --fts-block-gray-5: #232425;
  --fts-block-danger: #f44336;
  --fts-block-start-background: #00b74a;
  --fts-block-for-background: #1e88e5;
  --fts-block-condition-background: #ffa900;
  --fts-block-action-background: #fff;
  --fts-block-variable-border: #4caf50;
  --fts-block-variable-background: #{rgba(#4caf50, 0.15)};
}
</style>